/**
 * This JS manipulates the states of the UI elements in the following files:
 * /View/Commerce/Checkout/EnrollmentConsents.cshtml
 * /View/Commerce/Checkout/EnrollmentConsentBlock.cshtml
 **/
import {initToggle} from '../checkout/toggle-content';

const _cfg = document.getElementById('consent-readmore-config');
const CONSENT_DESKTOP_MAX = parseInt(_cfg && _cfg.dataset.desktopMax, 10) || 150;
const CONSENT_MOBILE_MAX = parseInt(_cfg && _cfg.dataset.mobileMax, 10) || 90;
const MOBILE_BREAKPOINT = parseInt(_cfg && _cfg.dataset.mobileBreakpoint, 10) || 768;

function getConsentMaxChars() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches ? CONSENT_MOBILE_MAX : CONSENT_DESKTOP_MAX;
}

function isMobileViewport() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
}

// returns sentence end position within ±tolerance chars of maxChars, or null if none in range
function findNearestSentenceEnd(text, maxChars, strictMax, tolerance = 10) {
    const sentenceEndRegex = /\.(?=[\s\u00A0]|$)/g;
    let match;
    let before = -1;
    let after = -1;

    while ((match = sentenceEndRegex.exec(text)) !== null) {
        const pos = match.index + 1;
        if(pos <= maxChars) {
            before = pos;
        } else {
            after = pos;
            break;
        }
    }

    if(strictMax) {
        return (before !== -1 && maxChars - before <= tolerance) ? before : null;
    }

    const beforeDist = before !== -1 ? maxChars - before : Infinity;
    const afterDist = after !== -1 ? after - maxChars : Infinity;

    if(beforeDist <= tolerance && afterDist <= tolerance) return beforeDist <= afterDist ? before : after;
    if(beforeDist <= tolerance) return before;
    if(afterDist <= tolerance) return after;
    return null;
}
// shared cut-point calculation used by both truncateConsentHTML and visibility checks
function computeEffectiveCutAt(plainText, maxChars) {
    const sentenceEnd = findNearestSentenceEnd(plainText, maxChars, isMobileViewport());
    if(sentenceEnd !== null) return { cutAt: sentenceEnd, addEllipsis: false };
    const nextSpace = plainText.indexOf(' ', maxChars);
    // if no space follows, include the last word fully rather than slicing mid-word
    const cutAt = nextSpace !== -1 ? nextSpace : plainText.length;
    return { cutAt, addEllipsis: cutAt < plainText.length };
}
function truncateConsentHTML(html, maxChars) {
    if(!html) return html;

    const plainText = $('<div>').html(html).text();
    if(plainText.length <= maxChars) return html;

    // snap to sentence end within ±10 chars; otherwise complete the word and add ellipsis
    const { cutAt, addEllipsis } = computeEffectiveCutAt(plainText, maxChars);

    if(cutAt >= plainText.length) return html;

    let charCount = 0;
    let output = '';
    let openTags = [];
    const tagRegex = /<\/?[^>]+>/g;
    const selfClosingTags = /^(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)$/i;
    const entityRegex = /&[a-zA-Z0-9#]+;/g;

    html = html.replace(/<!--[\s\S]*?-->/g, '');
    const parts = html.split(tagRegex);
    let match;
    let currentIndex = 0;

    while ((match = tagRegex.exec(html)) !== null) {
        const tag = match[0];
        const tagNameMatch = tag.match(/^<\/?([^\s/>]+)/);
        const tagName = tagNameMatch ? tagNameMatch[1].toLowerCase() : null;
        const part = parts.shift();

        if(part) {
            // replace each entity with one char to get the true plain-text length
            const textLen = part.replace(entityRegex, 'X').length;
            if(charCount + textLen <= cutAt) {
                output += part;
                charCount += textLen;
            } else {
                // walk the raw part to find the raw position equal to `remaining` plain-text chars
                const remaining = cutAt - charCount;
                const entityWalk = /&[a-zA-Z0-9#]+;/g;
                let rawPos = 0;
                let plainCount = 0;
                while (rawPos < part.length && plainCount < remaining) {
                    entityWalk.lastIndex = rawPos;
                    const em = entityWalk.exec(part);
                    if(em && em.index === rawPos) {
                        plainCount++;
                        rawPos += em[0].length;
                    } else {
                        plainCount++;
                        rawPos++;
                    }
                }
                output += part.slice(0, rawPos);
                charCount = cutAt;
            }
        }

        if(charCount >= cutAt) break;

        if(tagName) {
            if(tag[1] === '/') {
                if(openTags.length && openTags[openTags.length - 1] === tagName) {
                    openTags.pop();
                }
                output += tag;
            } else if(!selfClosingTags.test(tagName)) {
                openTags.push(tagName);
                output += tag;
            } else {
                output += tag;
            }
        }
        currentIndex = match.index + tag.length;
    }

    if(charCount < cutAt) {
        output += html.slice(currentIndex, currentIndex + (cutAt - charCount));
    }

    if(addEllipsis) output += '...';

    while (openTags.length) {
        output += `</${openTags.pop()}>`;
    }

    return output;
}

function collapseConsentReadMore($block) {
    const $body = $block.find('.mod-checkbox__label-body');
    const $btn = $block.find('.consent__read-more-btn');
    const fullHTML = $body.data('consent-full-html');
    if(!fullHTML || !$btn.data('expanded')) return;
    $body.html(truncateConsentHTML(fullHTML, getConsentMaxChars()));
    $btn.find('.consent__read-more-label').text($btn.data('read-more-label'));
    $btn.attr('aria-expanded', 'false').data('expanded', false);
}

function initConsentReadMore() {
    const maxChars = getConsentMaxChars();

    $('.mod-consent-block').each(function () {
        const $block = $(this);
        const $body = $block.find('.mod-checkbox__label-body');
        const $btn = $block.find('.consent__read-more-btn');

        if(!$body.length || !$btn.length) return;

        if(!$body.data('consent-full-html')) {
            $body.data('consent-full-html', $body.html());
        }

        const fullHTML = $body.data('consent-full-html');
        const plainText = $('<div>').html(fullHTML).text();
        const { cutAt: effectiveCut } = plainText.length > maxChars
            ? computeEffectiveCutAt(plainText, maxChars)
            : { cutAt: plainText.length };

        if(effectiveCut < plainText.length) {
            $body.html(truncateConsentHTML(fullHTML, maxChars));
            $btn.removeClass('d-none');
            $btn.find('.consent__read-more-label').text($btn.data('read-more-label'));
            $btn.attr('aria-expanded', 'false').data('expanded', false);
        } else {
            $btn.addClass('d-none');
        }
    });
}

function handleConsentReadMoreClick() {
    const $btn = $(this);
    const $block = $btn.closest('.mod-consent-block');
    const $body = $block.find('.mod-checkbox__label-body');
    const fullHTML = $body.data('consent-full-html');
    const isExpanded = $btn.data('expanded');

    if(isExpanded) {
        $body.html(truncateConsentHTML(fullHTML, getConsentMaxChars()));
        $btn.find('.consent__read-more-label').text($btn.data('read-more-label'));
        $btn.attr('aria-expanded', 'false').data('expanded', false);
    } else {
        $body.html(fullHTML);
        $btn.find('.consent__read-more-label').text($btn.data('read-less-label'));
        $btn.attr('aria-expanded', 'true').data('expanded', true);
    }
}

export function initConsentReadMoreBehavior() {
    initConsentReadMore();
    $(document).on('click', '.consent__read-more-btn', handleConsentReadMoreClick);

    let resizeTimer;
    $(window).on('resize.consentReadMore', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            const maxChars = getConsentMaxChars();
            $('.mod-consent-block').each(function () {
                const $block = $(this);
                const $body = $block.find('.mod-checkbox__label-body');
                const $btn = $block.find('.consent__read-more-btn');
                const fullHTML = $body.data('consent-full-html');
                if(!fullHTML) return;
                if($btn.data('expanded')) return;
                const plainText = $('<div>').html(fullHTML).text();
                const { cutAt: effectiveCut } = plainText.length > maxChars
                    ? computeEffectiveCutAt(plainText, maxChars)
                    : { cutAt: plainText.length };
                if(effectiveCut < plainText.length) {
                    $body.html(truncateConsentHTML(fullHTML, maxChars));
                    $btn.removeClass('d-none');
                } else {
                    $body.html(fullHTML);
                    $btn.addClass('d-none');
                }
            });
        }, 200);
    });
}

function uncheckConsents(tab) {
    // uncheck all consents within the tab using jquery
    $(`${tab} input[type=checkbox]`).each(function () {
        $(this).prop('checked', false);
    });
    $(`${tab} span.consent-error`).each(function () {
        $(this).css('display', 'none');
    });
    // collapse any expanded consent descriptions when switching tabs
    $(`${tab} .mod-consent-block`).each(function () {
        collapseConsentReadMore($(this));
    });
    initToggle();
}

export function watchConsentTabs() {
    $.fn.onClassChange = function (cb) {
        return $(this).each((_, el) => {
            // eslint-disable-next-line no-undef
            new MutationObserver(mutations => {
                mutations.forEach(mutation => cb && cb(mutation.target, $(mutation.target).prop(mutation.attributeName)));
            }).observe(el, {
                attributes: true,
                attributeFilter: ['class'] // only listen for class attribute changes
            });
        });
    };

    ['first', 'second'].forEach((prefix) => {
        let tabName = `#${prefix}-pref-consents`;
        $(tabName).onClassChange(() => uncheckConsents(tabName));
    });
}
